import { createContext, useContext, ReactNode, useState, useCallback } from 'react';

type ModalType = 'newNote' | 'openNote' | null;

interface NotesModalContextValue {
  isModalOpen: boolean;
  activeModal: ModalType;
  keepModalOpen: boolean;
  showUnsavedDialog: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  setActiveModal: (modal: ModalType) => void;
  setKeepModalOpen: (keep: boolean) => void;
  setShowUnsavedDialog: (show: boolean) => void;
  openNewNoteModal: () => void;
  openEditNoteModal: () => void;
  closeModal: () => void;
  handleModalChange: (isOpen: boolean) => void;
}

export type { ModalType };

const NotesModalContext = createContext<NotesModalContextValue | undefined>(undefined);

interface NotesModalProviderProps {
  children: ReactNode;
}

export const NotesModalProvider: React.FC<NotesModalProviderProps> = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [keepModalOpen, setKeepModalOpen] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  const openNewNoteModal = useCallback(() => {
    setActiveModal('newNote');
    setIsModalOpen(true);
  }, []);

  const openEditNoteModal = useCallback(() => {
    setActiveModal('openNote');
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setActiveModal(null);
    setIsModalOpen(false);
    setKeepModalOpen(false);
    setShowUnsavedDialog(false);
  }, []);

  const handleModalChange = useCallback((isOpen: boolean) => {
    if (isOpen) {
      return setIsModalOpen(true);
    }
    if (keepModalOpen) {
      return setShowUnsavedDialog(true);
    }
    closeModal();
  }, [keepModalOpen, closeModal]);

  const contextValue: NotesModalContextValue = {
    isModalOpen,
    activeModal,
    keepModalOpen,
    showUnsavedDialog,
    setIsModalOpen,
    setActiveModal,
    setKeepModalOpen,
    setShowUnsavedDialog,
    openNewNoteModal,
    openEditNoteModal,
    closeModal,
    handleModalChange
  };

  return <NotesModalContext.Provider value={contextValue}>{children}</NotesModalContext.Provider>;
};

export const useNotesModalContext = (): NotesModalContextValue => {
  const context = useContext(NotesModalContext);
  if (!context) {
    throw new Error('useNotesModalContext must be used within NotesModalProvider');
  }
  return context;
};
